/**
 * 🏛️ WONDER SITES - 3-tile map structures that unlock wonders
 *
 * Rules:
 * - Wonder sites spawn as 3-tile triangles on the world map
 * - Must be 10+ tiles from kingdom to spawn
 * - 1 guaranteed wonder per tier, 2 random chance
 * - Discovering any 1 tile auto-reveals all 3
 * - Must discover site before building wonder in WonderPanel
 */

import { WONDERS } from '../wonders';

// ===========================================
// CONFIGURATION
// ===========================================

export const WONDER_SITE_CONFIG = {
  // Minimum distance from kingdom (0,0) to spawn a wonder site
  minDistanceFromKingdom: 10,

  // Spawn chance for non-guaranteed wonders (per tile explored)
  randomSpawnChance: 0.05, // 5% per tile

  // Chance to spawn guaranteed wonder each tile (before pity kicks in)
  guaranteedBaseChance: 0.15, // 15% per tile

  // Pity threshold - force spawn at this % of exploration limit
  pityThreshold: 0.8, // 80%

  // Which wonders require map site discovery (by tier)
  // Format: { wonderId: { spawnWeight: number } }
  // Higher spawnWeight = more likely to be the guaranteed wonder
  siteBoundWonders: {
    // Tier 1 - Primitive Age
    1: {
      stonehenge: { spawnWeight: 10 },
      great_bonfire: { spawnWeight: 8 },
      tribal_monument: { spawnWeight: 8 },
    },
    // Tier 2 - Stone Age
    2: {
      great_granary: { spawnWeight: 10 },
      stone_citadel: { spawnWeight: 8 },
      sacred_grove: { spawnWeight: 8 },
    },
    // Tier 3 - Bronze Age
    3: {
      hanging_gardens: { spawnWeight: 10 },
      great_library: { spawnWeight: 8 },
      colosseum: { spawnWeight: 8 },
    },
    // Add more tiers as needed...
  },
};

// ===========================================
// WONDER SITE BIOME
// ===========================================

export const WONDER_SITE_BIOME = {
  id: 'wonderSite',
  name: 'Ancient Wonder',
  icon: '🏛️',
  description: 'Part of a legendary structure. Discover all tiles to unlock the wonder.',
  color: '#6a5d4d', // Weathered stone color
  minAge: 1,
  spawnWeight: 0, // Never spawns naturally - only via wonder site system
  isWonderSite: true,
  canBuildOutpost: false,
  gatherableResources: {
    knowledge: { baseAmount: 3, richness: 1.0 },
  },
  entryEvents: [
    {
      id: 'wonder_mystery',
      weight: 50,
      text: 'Ancient stones mark this sacred place. You sense greater power nearby...',
      rewards: { knowledge: 5 },
    },
    {
      id: 'wonder_fragment',
      weight: 30,
      text: 'You discover fragments of an ancient inscription.',
      rewards: { knowledge: 10 },
    },
    {
      id: 'wonder_vision',
      weight: 20,
      text: 'A vision of a magnificent wonder flashes before your eyes!',
      rewards: { knowledge: 15 },
    },
  ],
};

// ===========================================
// TRIANGLE PATTERNS (6 orientations)
// ===========================================

/**
 * Get all 6 possible triangle patterns around an anchor hex
 * Each pattern is an array of 3 coordinates that form a valid triangle
 * (all 3 hexes are mutually adjacent)
 */
export const TRIANGLE_PATTERNS = [
  // Pattern 0: Up-right triangle
  [{ dq: 0, dr: 0 }, { dq: 1, dr: 0 }, { dq: 0, dr: -1 }],
  // Pattern 1: Right triangle
  [{ dq: 0, dr: 0 }, { dq: 1, dr: -1 }, { dq: 1, dr: 0 }],
  // Pattern 2: Down-right triangle
  [{ dq: 0, dr: 0 }, { dq: 1, dr: 0 }, { dq: 0, dr: 1 }],
  // Pattern 3: Down-left triangle
  [{ dq: 0, dr: 0 }, { dq: 0, dr: 1 }, { dq: -1, dr: 1 }],
  // Pattern 4: Left triangle
  [{ dq: 0, dr: 0 }, { dq: -1, dr: 1 }, { dq: -1, dr: 0 }],
  // Pattern 5: Up-left triangle
  [{ dq: 0, dr: 0 }, { dq: -1, dr: 0 }, { dq: 0, dr: -1 }],
];

// ===========================================
// HELPER FUNCTIONS
// ===========================================

/**
 * Get all site-bound wonder IDs for a specific tier
 */
export function getSiteBoundWondersForTier(tier) {
  const tierWonders = WONDER_SITE_CONFIG.siteBoundWonders[tier];
  if (!tierWonders) return [];
  return Object.keys(tierWonders);
}

/**
 * Get all site-bound wonder IDs up to and including the given tier
 */
export function getAllSiteBoundWonders(maxTier = 11) {
  const wonders = [];
  for (let tier = 1; tier <= maxTier; tier++) {
    wonders.push(...getSiteBoundWondersForTier(tier));
  }
  return wonders;
}

/**
 * Check if a wonder requires a site discovery
 */
export function isWonderSiteBound(wonderId) {
  for (const tierWonders of Object.values(WONDER_SITE_CONFIG.siteBoundWonders)) {
    if (tierWonders[wonderId]) return true;
  }
  return false;
}

/**
 * Get the tier of a site-bound wonder
 */
export function getWonderSiteTier(wonderId) {
  for (const [tier, tierWonders] of Object.entries(WONDER_SITE_CONFIG.siteBoundWonders)) {
    if (tierWonders[wonderId]) return parseInt(tier);
  }
  return null;
}

/**
 * Pick a random wonder from a tier based on spawn weights
 * @param {number} tier - The tier to pick from
 * @param {string[]} excludeIds - Wonder IDs to exclude (already spawned/built)
 */
export function pickRandomWonderFromTier(tier, excludeIds = []) {
  const tierWonders = WONDER_SITE_CONFIG.siteBoundWonders[tier];
  if (!tierWonders) return null;

  // Filter out excluded wonders
  const available = Object.entries(tierWonders)
    .filter(([id]) => !excludeIds.includes(id));

  if (available.length === 0) return null;

  // Weighted random selection
  const totalWeight = available.reduce((sum, [, cfg]) => sum + cfg.spawnWeight, 0);
  let roll = Math.random() * totalWeight;

  for (const [wonderId, cfg] of available) {
    roll -= cfg.spawnWeight;
    if (roll <= 0) return wonderId;
  }

  // Fallback to first available
  return available[0][0];
}

/**
 * Pick a random available wonder from any tier up to maxTier
 * @param {number} maxTier - Maximum tier to consider
 * @param {string[]} excludeIds - Wonder IDs to exclude
 */
export function pickRandomAvailableWonder(maxTier, excludeIds = []) {
  // Collect all available wonders
  const available = [];
  for (let tier = 1; tier <= maxTier; tier++) {
    const tierWonders = WONDER_SITE_CONFIG.siteBoundWonders[tier];
    if (!tierWonders) continue;

    for (const [wonderId, cfg] of Object.entries(tierWonders)) {
      if (!excludeIds.includes(wonderId)) {
        available.push({ wonderId, tier, weight: cfg.spawnWeight });
      }
    }
  }

  if (available.length === 0) return null;

  // Weighted random selection
  const totalWeight = available.reduce((sum, w) => sum + w.weight, 0);
  let roll = Math.random() * totalWeight;

  for (const { wonderId } of available) {
    const weight = available.find(w => w.wonderId === wonderId).weight;
    roll -= weight;
    if (roll <= 0) return wonderId;
  }

  return available[0].wonderId;
}

/**
 * Generate triangle coordinates from anchor position
 * @param {number} anchorQ - Anchor hex Q coordinate
 * @param {number} anchorR - Anchor hex R coordinate
 * @param {number} patternIndex - Which pattern to use (0-5), or random if not specified
 */
export function generateTriangleCoords(anchorQ, anchorR, patternIndex = null) {
  const index = patternIndex ?? Math.floor(Math.random() * TRIANGLE_PATTERNS.length);
  const pattern = TRIANGLE_PATTERNS[index];

  return pattern.map(({ dq, dr }) => ({
    q: anchorQ + dq,
    r: anchorR + dr,
  }));
}

/**
 * Get wonder data for a site-bound wonder
 */
export function getWonderData(wonderId) {
  return WONDERS[wonderId] || null;
}
